// hooks/useFirestoreHistory.js
import { useState, useEffect, useCallback } from 'react';
import {
  collection, addDoc, deleteDoc, doc,
  query, orderBy, limit, serverTimestamp, getDocs
} from 'firebase/firestore';
import { db, auth } from '../firebase';

const useFirestoreHistory = () => {
  const [chatHistory, setChatHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // FIX Bug 4: derive userId directly from auth.currentUser instead of
  // maintaining a separate onAuthStateChanged listener that can briefly
  // go out of sync with the useAuth() context used in DMCAGenerator.
  const [userId, setUserId] = useState(() => auth.currentUser?.uid || null);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged((user) => {
      if (user) {
        setUserId(user.uid);
      } else {
        setUserId(null);
        setChatHistory([]);
        setIsLoading(false);
      }
    });
    return () => unsub();
  }, []);

  const loadHistory = useCallback(async () => {
    const uid = auth.currentUser?.uid;
    if (!uid) { setIsLoading(false); return; }
    setIsLoading(true);
    try {
      const ref = collection(db, 'users', uid, 'dmca_history');
      const q = query(ref, orderBy('timestamp', 'desc'), limit(20));
      const snap = await getDocs(q);
      const items = snap.docs.map(d => ({
        id: d.id,
        ...d.data(),
        timestamp: d.data().timestamp?.toDate() || new Date()
      }));
      setChatHistory(items);
    } catch (e) {
      console.error('Error loading history:', e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (userId) loadHistory();
  }, [userId, loadHistory]);

  const saveChatSession = useCallback(async (title, letters, aiResponse) => {
    const uid = auth.currentUser?.uid;
    if (!uid) return null;
    try {
      const ref = collection(db, 'users', uid, 'dmca_history');
      const shortTitle = title.length > 60 ? title.substring(0, 60) + '...' : title;

      // FIX 1: Save the COMPLETE letter payload including knownAgent and ALL
      // formData fields (claimantName, claimantEmail, claimantAddress,
      // workDescription, workType, originalWorkUrl, infringingUrl, platform).
      // Previously knownAgent was omitted so verified-agent info was lost on reload.
      const docRef = await addDoc(ref, {
        title: shortTitle,
        qrCodes: letters.map(l => ({
          id: l.id,
          letterText: l.letterText,
          // Save every formData field so the form can be pre-filled on reload
          formData: {
            claimantName:    l.formData.claimantName    || '',
            claimantEmail:   l.formData.claimantEmail   || '',
            claimantAddress: l.formData.claimantAddress || '',
            workDescription: l.formData.workDescription || '',
            workType:        l.formData.workType        || '',
            originalWorkUrl: l.formData.originalWorkUrl || '',
            infringingUrl:   l.formData.infringingUrl   || '',
            platform:        l.formData.platform        || '',
          },
          // FIX 1b: persist knownAgent so "Verified agent" badge & email
          // script survive a page reload / session load
          knownAgent:      l.knownAgent      || null,
          timestamp:       l.timestamp,
          platform:        l.platform,
          infringingUrl:   l.infringingUrl,
        })),
        aiResponse: aiResponse || '',
        timestamp: serverTimestamp(),
        createdAt: new Date().toISOString(),
      });

      await loadHistory();
      return docRef.id;
    } catch (e) {
      console.error('Error saving session:', e);
      return null;
    }
  }, [loadHistory]);

  const deleteChatSession = useCallback(async (sessionId) => {
    const uid = auth.currentUser?.uid;
    if (!uid) return false;
    try {
      await deleteDoc(doc(db, 'users', uid, 'dmca_history', sessionId));
      await loadHistory();
      return true;
    } catch (e) {
      console.error('Error deleting session:', e);
      return false;
    }
  }, [loadHistory]);

  const getRelativeTime = (date) => {
    const diffMs = new Date() - date;
    const mins  = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMs / 3600000);
    const days  = Math.floor(diffMs / 86400000);
    if (mins  < 1)   return 'Just now';
    if (mins  < 60)  return `${mins}m ago`;
    if (hours < 24)  return `${hours}h ago`;
    if (days  === 1) return 'Yesterday';
    if (days  < 7)   return `${days}d ago`;
    return date.toLocaleDateString();
  };

  return {
    chatHistory,
    isLoading,
    saveChatSession,
    deleteChatSession,
    getRelativeTime,
    userId,
    refreshHistory: loadHistory,
  };
};

export default useFirestoreHistory;